import { AUTH_MODE, BUILD_LABEL, HAS_SUPABASE } from './config';
import { AuthClient, type AuthSnapshot } from './auth';
import {
  ApiError,
  createRoom,
  fetchLeaderboard,
  fetchMe,
  joinRoom,
  updateDisplayName,
} from './api';
import { AudioEngine } from './audio';
import { eyeMark, startGrain } from './effects';
import { esc } from './html';
import { OnlineSession, startQuickMatch, type QuickMatchHandle } from './online';
import type { Profile } from '../shared/types';
import { PROTOCOLS, PROTOCOL_ORDER } from '../shared/protocols';
import { rankTitle } from '../shared/elo';
import { sanitizeDisplayName, sanitizeRoomCode } from '../shared/names';
import { applyGraphicsQuality, applyFrameTarget, isFrameTarget, isGraphicsQuality, loadSettings, saveSettings, type Settings } from './settings';
import { formatLatency, runNetworkDiagnostics } from './diagnostics';
import { SoloSession } from './solo';
import { renderArchiveModal } from './lore/archive';
import { LoreRepository } from './lore/repository';
import type { LoreSection } from './lore/types';
import { FpsMonitor } from './fps';

type ViewName = 'gate' | 'home' | 'auth' | 'lobby' | 'game';
type AuthTab = 'login' | 'signup';
type AuthIntent = 'home' | 'lobby';

export class App {
  private readonly root: HTMLElement;
  private view!: HTMLElement;
  private modalRoot!: HTMLElement;
  private toastEl!: HTMLElement;
  private auth = new AuthClient();
  private audio: AudioEngine;
  private settings: Settings;
  private lore = new LoreRepository();
  private archiveSection: LoreSection = 'protocols';
  private archiveDocId: string | null = null;
  private profile: Profile | null = null;
  private viewName: ViewName = 'gate';
  private authTab: AuthTab = 'login';
  private authIntent: AuthIntent | null = null;
  private solo: SoloSession | null = null;
  private online: OnlineSession | null = null;
  private quickMatch: QuickMatchHandle | null = null;
  private quickSearching = false;
  private modalDismissible = true;
  private modalOnClose: (() => void) | null = null;
  private profilePromise: Promise<void> | null = null;
  private leaderboardRequest = 0;
  private stopGrain: (() => void) | null = null;
  private fps: FpsMonitor | null = null;
  private toastTimer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.settings = loadSettings();
    this.audio = new AudioEngine(this.settings);
  }

  async init(): Promise<void> {
    applyGraphicsQuality(this.settings.graphicsQuality);
    applyFrameTarget(this.settings.frameTarget);
    this.renderShell();
    this.stopGrain = startGrain(this.root.querySelector<HTMLCanvasElement>('#grain')!);
    this.fps = new FpsMonitor(this.root.querySelector<HTMLElement>('#fps-meter')!);
    this.fps.setVisible(this.settings.showFps);
    this.view.addEventListener('click', this.onViewClick);
    this.view.addEventListener('submit', this.onViewSubmit);
    this.modalRoot.addEventListener('click', this.onModalClick);
    this.modalRoot.addEventListener('submit', this.onModalSubmit);
    this.modalRoot.addEventListener('input', this.onModalInput);
    this.modalRoot.addEventListener('change', this.onModalInput);
    document.addEventListener('keydown', this.onGlobalKeyDown);
    this.auth.onChange((snapshot) => {
      void this.onAuthChange(snapshot);
    });
    this.auth.onPasswordRecovery(() => {
      this.openPasswordModal(true);
    });
    renderAuthModeBadge();
    this.lore.setTokenProvider(() => this.auth.token());
    const params = new URLSearchParams(location.search);
    if (params.has('code') || location.hash.includes('access_token')) {
      this.showToast('正在校验受试者会话…');
    }
    this.renderGate();
    await this.auth.init();
    void this.lore.syncRemote();
  }

  private renderShell(): void {
    this.root.innerHTML = `
      <div class="world" data-view="gate">
        <div class="world-backdrop" aria-hidden="true"></div>
        <div class="world-tint" aria-hidden="true"></div>
        <canvas class="grain" id="grain" aria-hidden="true"></canvas>
        <div class="scanlines" aria-hidden="true"></div>
        <div class="vignette" aria-hidden="true"></div>
        <main class="view" id="view"></main>
        <div class="global-toast" id="global-toast" role="status" aria-live="polite"></div>
        <div class="fps-meter" id="fps-meter" hidden><b data-fps-value>--</b><span>FPS</span></div>
      </div>
      <div class="modal-root" id="modal-root" aria-live="polite"></div>
    `;
    this.view = this.root.querySelector<HTMLElement>('#view')!;
    this.modalRoot = this.root.querySelector<HTMLElement>('#modal-root')!;
    this.toastEl = this.root.querySelector<HTMLElement>('#global-toast')!;
  }

  private setScreen(name: ViewName, html: string): void {
    this.viewName = name;
    document.body.dataset.view = name;
    this.view.classList.remove('is-switching');
    this.view.innerHTML = html;
    void this.view.offsetWidth;
    this.view.classList.add('is-switching');
    const world = this.root.querySelector<HTMLElement>('.world');
    if (world) world.dataset.view = name;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  private renderGate(): void {
    this.setScreen(
      'gate',
      `
      <section class="gate">
        <div class="gate-frame">
          <div class="gate-topline">
            <span class="micro-label">VESPER BEHAVIOURAL RESEARCH / 06</span>
            <span class="micro-label">ROOM 2F-Δ</span>
          </div>
          <div class="gate-eye">${eyeMark('gate-eye-mark')}</div>
          <span class="gate-warning-tag">NOTICE / 06</span>
          <h1 class="gate-title">受试前告知</h1>
          <p class="gate-sub">SUBJECTS / 受试者</p>
          <div class="gate-copy">
            <p>本终端包含<strong>强闪烁、突发音效、电击与医疗恐怖意象</strong>。光敏性癫痫患者、心血管疾病患者、孕妇及未成年人请勿进入。</p>
            <p><strong>本作不提供低刺激模式、字幕或色盲辅助。</strong>如果你对闪烁、恐怖内容、强声或颜色识别有困难，请不要开始。</p>
            <p>校准过程中，系统会记录你的反应时间与错误输入。污染值仅用于本地演出，不会影响现实设备。</p>
            <p>建议使用耳机，关闭外部照明，保持坐姿。你可以随时按 <kbd>ESC</kbd> 终止链路。</p>
          </div>
          <div class="gate-checks">
            <span><i></i> PHOTOSENSITIVE WARNING</span>
            <span><i></i> CLINICAL HORROR</span>
            <span><i></i> LOUD AUDIO</span>
            <span><i></i> VOICE ALWAYS ON</span>
          </div>
          <div class="gate-actions">
            <button class="btn btn-danger btn-large" type="button" data-action="enter">
              <span>接受校准</span><small>ACCEPT &amp; ENTER</small>
            </button>
          </div>
          <p class="gate-foot">进入即表示你已阅读并接受上述风险。颜色广播始终开启；无法接受强刺激或颜色识别要求时，请直接关闭页面。</p>
          <div class="gate-footer-line">
            <span>VESPER // CHROMA-06</span>
            <span>LOCAL SESSION · ${esc(BUILD_LABEL)}</span>
          </div>
        </div>
      </section>`,
    );
  }

  private renderHome(): void {
    const profile = this.profile;
    const account = profile
      ? `<button class="account-chip" type="button" data-action="lobby">
           <i class="live-dot"></i>
           <span>${esc(profile.name)}</span>
           <b>${profile.rating}</b>
         </button>`
      : AUTH_MODE === 'none'
        ? `<span class="account-chip is-disabled">ONLINE MODULE OFFLINE</span>`
        : `<button class="account-chip is-empty" type="button" data-action="auth">
             <span>SIGN IN // 登录</span>
           </button>`;
    const protocols = PROTOCOL_ORDER.map(
      (id) => `
      <li>
        <span class="protocol-roman">${roman(id)}</span>
        <span><b>${PROTOCOLS[id].nameZh}</b><small>${PROTOCOLS[id].nameEn}</small></span>
        <em>${PROTOCOLS[id].timeLimitMs}ms</em>
      </li>`,
    ).join('');

    this.setScreen(
      'home',
      `
      <section class="page home">
        ${this.topbar(account)}
        <div class="home-grid">
          <div class="hero">
            <div class="hero-eye" aria-hidden="true">${eyeMark('')}</div>
            <div class="classification"><span>CLASSIFIED</span> / PROJECT CHROMA / COGNITIVE DIVISION</div>
            <h1 class="hero-title is-subjects"><span>SUBJECTS</span><span class="outline">受试者</span></h1>
            <div class="hero-cn">六段认知校准 <small>PROJECT CHROMA / SITE 06</small></div>
            <p class="hero-lede">不要读。<em>选择你实际看到的颜色。</em></p>
            <p class="hero-body">
              字义与墨色会互相背叛。四块屏幕里只有一块或几块发白，那是同步源；屏幕上那行字的墨色决定目标，按键上写的颜色名就是答案，按键自己的字色是干扰。
              六个协议不是终点，而是一套已经离开 Site 06、进入学校、工厂与广播系统的方法。
              在 90 秒内抢占对手的确认权；读错一次烧一根灯管，三根烧完，这一侧的人就垮了。
            </p>
            <div class="hero-actions">
              <button class="btn btn-primary btn-large" type="button" data-action="practice">
                <span>开始校准</span><small>PRACTICE / LOCAL</small>
              </button>
              <button class="btn btn-secondary" type="button" data-action="online">
                <span>联机受试</span><small>1V1 LIVE DUEL</small>
              </button>
            </div>
            <div class="hero-meter">
              <span class="micro-label">PROTOCOL THRESHOLDS / 协议阈值</span>
              <ol class="protocol-preview">${protocols}</ol>
            </div>
          </div>
          <aside class="intake">
            <div class="intake-stamp">INTAKE<br/>06-C</div>
            <div class="intake-head">
              <span class="micro-label">SUBJECT INTAKE / 受试者登记</span>
              <span class="doc-number">FORM 06-B</span>
            </div>
            <div class="intake-subject">
              <div class="intake-photo">${eyeMark('')}</div>
              <div>
                <strong>${profile ? esc(profile.name) : 'UNREGISTERED'}</strong>
                <span>${profile ? `${rankTitle(profile.rating)} / RATING ${profile.rating}` : '未建立联机档案'}</span>
              </div>
            </div>
            <dl class="intake-data">
              <div><dt>PROTOCOL RANGE</dt><dd>I — VI</dd></div>
              <div><dt>SESSION</dt><dd>90 SEC</dd></div>
              <div><dt>CONTAMINATION</dt><dd>SHARED</dd></div>
              <div><dt>AUDIO FEED</dt><dd>ZH / EN</dd></div>
            </dl>
            <p class="intake-warning">
              <b>注意</b>：污染值会改变画面与声音，但不会改变服务器判定。
              所有规则变更都会提前广播。若感到不适，立即中止。
            </p>
            <div class="intake-actions">
              ${
                profile
                  ? `<button class="btn btn-ghost btn-small" type="button" data-action="lobby">进入大厅 / LOBBY</button>
                     <button class="link-button" type="button" data-action="signout">登出</button>`
                  : AUTH_MODE === 'none'
                    ? `<span class="inline-note">ONLINE AUTH NOT CONFIGURED</span>`
                    : `<button class="btn btn-ghost btn-small" type="button" data-action="auth">登录 / SIGN IN</button>`
              }
            </div>
          </aside>
        </div>
        <footer class="home-footer">
          <span>© VESPER BEHAVIOURAL RESEARCH / FICTIONAL INTERFACE</span>
          <span>PHOTOSENSITIVE · CLINICAL HORROR · LOUD AUDIO</span>
          <button class="link-button" type="button" data-action="settings">校准设置 / SETTINGS</button>
        </footer>
      </section>`,
    );
  }

  private renderAuth(message = ''): void {
    if (AUTH_MODE === 'none') {
      this.setScreen(
        'auth',
        `
        <section class="page auth-page">
          ${this.topbar('')}
          <div class="auth-grid">
            ${this.authBrief()}
            <main class="dossier auth-card">
              <span class="doc-number">FORM 06-B / SUBJECT CREDENTIALS</span>
              <h1>联机模块未配置</h1>
              <p class="auth-intro">账号服务尚未注入。单人校准不受影响；要启用联机，需要配置 Supabase 与 Cloudflare 部署变量。</p>
              <div class="notice-box">
                <b>环境项</b>
                <code>VITE_SUPABASE_URL</code>
                <code>VITE_SUPABASE_ANON_KEY</code>
                <code>SUPABASE_URL / SUPABASE_ANON_KEY (Worker)</code>
              </div>
              <button class="btn btn-ghost" type="button" data-action="home">返回主菜单 / RETURN</button>
            </main>
          </div>
        </section>`,
      );
      return;
    }

    if (this.auth.mode === 'dev') {
      this.setScreen(
        'auth',
        `
        <section class="page auth-page">
          ${this.topbar('')}
          <div class="auth-grid">
            ${this.authBrief()}
            <main class="dossier auth-card">
              <span class="doc-number">FORM 06-B / LOCAL DEVELOPMENT</span>
              <h1>本机测试身份</h1>
              <p class="auth-intro">当前未配置 Supabase，正在使用开发登录。该身份只存在于当前标签页，刷新或关闭后失效。</p>
              <form data-form="dev-auth">
                <label class="field">
                  <span>受试者代号 / SUBJECT ALIAS</span>
                  <input name="displayName" maxlength="16" autocomplete="nickname" required placeholder="例如：R-06" />
                  <small>2–16 个字符，可使用中文、英文、数字。</small>
                </label>
                <p class="form-message" data-form-message aria-live="polite">${esc(message)}</p>
                <button class="btn btn-primary" type="submit">建立本机档案 / ENTER</button>
              </form>
              <p class="auth-fineprint">生产环境必须使用 Supabase 邮箱账号；开发身份不会被线上 Worker 接受。</p>
              <button class="link-button" type="button" data-action="home">返回主菜单</button>
            </main>
          </div>
        </section>`,
      );
      return;
    }

    this.setScreen(
      'auth',
      `
      <section class="page auth-page">
        ${this.topbar('')}
        <div class="auth-grid">
          ${this.authBrief()}
          <main class="dossier auth-card">
            <span class="doc-number">FORM 06-B / SUBJECT CREDENTIALS</span>
            <h1>受试者登记</h1>
            <p class="auth-intro">联机对局需要一个稳定档案，用于匹配、段位与战绩记录。邮箱只用于登录验证。</p>
            <div class="form-tabs" data-auth-tab="${this.authTab}">
              <button class="${this.authTab === 'login' ? 'is-active' : ''}" type="button" data-action="auth-tab" data-tab="login">已有档案 / SIGN IN</button>
              <button class="${this.authTab === 'signup' ? 'is-active' : ''}" type="button" data-action="auth-tab" data-tab="signup">新建档案 / REGISTER</button>
            </div>
            <form data-form="auth" data-auth-tab="${this.authTab}">
              <label class="field">
                <span>邮箱 / EMAIL</span>
                <input name="email" type="email" autocomplete="email" required placeholder="subject@vesper.invalid" />
              </label>
              <label class="field">
                <span>密码 / PASSPHRASE</span>
                <input name="password" type="password" minlength="8" autocomplete="current-password" required placeholder="至少 8 位" />
              </label>
              <label class="field signup-only">
                <span>受试者代号 / SUBJECT ALIAS</span>
                <input name="displayName" maxlength="16" autocomplete="nickname" placeholder="例如：R-06" />
                <small>2–16 个字符，将显示在段位与大厅中。</small>
              </label>
              <p class="form-message" data-form-message aria-live="polite">${esc(message)}</p>
              <button class="btn btn-primary" type="submit" data-auth-submit>
                ${this.authTab === 'login' ? '进入档案 / SIGN IN' : '提交登记 / REGISTER'}
              </button>
              <div class="form-links">
                <button class="link-button" type="button" data-action="forgot-password">忘记密码</button>
                <span>EMAIL AUTH · SUPABASE</span>
              </div>
            </form>
            <p class="auth-fineprint">连续失败会触发服务端限流。我们不会向第三方出售你的邮箱或对局数据。</p>
            <button class="link-button" type="button" data-action="home">返回主菜单</button>
          </main>
        </div>
      </section>`,
    );
  }

  private authBrief(): string {
    return `
      <aside class="auth-brief">
        <div class="auth-eye">${eyeMark('')}</div>
        <span class="micro-label">VESPER / CHROMA-06</span>
        <h2>“墨水不会说谎。<br/>文字会。”</h2>
        <p>登记完成后，你将获得一个段位、可追踪的对局记录，以及进入二楼斯特鲁普终端的权限。</p>
        <div class="auth-brief-list">
          <span><b>01</b> 找发白的屏</span>
          <span><b>02</b> 读那行字的墨色</span>
          <span><b>03</b> 按写着该颜色的键</span>
        </div>
        <span class="auth-brief-warning">NOTICE: 对联机档案的每一次终止都会计入战绩。</span>
      </aside>`;
  }

  private renderLobby(): void {
    const profile = this.profile;
    if (!profile) {
      void this.ensureProfileAndRender();
      return;
    }
    const wins = profile.wins;
    const losses = profile.losses;
    const draws = profile.draws;
    this.setScreen(
      'lobby',
      `
      <section class="page lobby-page">
        ${this.topbar(
          `<button class="account-chip" type="button" data-action="edit-name">
             <i class="live-dot"></i><span>${esc(profile.name)}</span><b>${profile.rating}</b>
           </button>`,
        )}
        <div class="lobby-heading">
          <div>
            <span class="eyebrow">SLEEP ROOM / SECOND FLOOR / TERMINAL 06</span>
            <h1>受试者大厅</h1>
            <p>选择一名受试者，或让系统替你寻找一个。规则只会在广播中改变。</p>
          </div>
          <div class="subject-file">
            <div class="file-photo">${eyeMark('')}</div>
            <div class="file-identity">
              <strong>${esc(profile.name)}</strong>
              <span>${esc(rankTitle(profile.rating))} / SUBJECT IN GOOD STANDING</span>
            </div>
            <div class="file-stats">
              <div><span>RATING</span><b>${profile.rating}</b></div>
              <div><span>W—L—D</span><b>${wins}—${losses}—${draws}</b></div>
              <div><span>SESSIONS</span><b>${profile.matches}</b></div>
            </div>
            <div class="file-actions">
              <button class="link-button" type="button" data-action="edit-name">编辑代号</button>
              ${
                this.auth.mode === 'supabase'
                  ? '<button class="link-button" type="button" data-action="change-password">修改密码</button>'
                  : ''
              }
              <button class="link-button" type="button" data-action="signout">登出</button>
            </div>
          </div>
        </div>

        <div class="lobby-grid">
          <section class="action-stack">
            <button class="action-card action-primary" type="button" data-action="quick-match">
              <span class="action-index">01</span>
              <span class="action-text"><b>快速匹配</b><small>QUICK MATCH / 1V1</small></span>
              <span class="action-arrow">→</span>
            </button>
            <div class="action-card">
              <span class="action-index">02</span>
              <div class="action-content">
                <b>创建房间</b>
                <small>CREATE PRIVATE LINK</small>
                <p>生成一枚房间码，等待另一名受试者接入。</p>
                <button class="btn btn-ghost btn-small" type="button" data-action="create-room">生成房间码 / CREATE</button>
              </div>
            </div>
            <form class="action-card" data-form="join-room">
              <span class="action-index">03</span>
              <div class="action-content">
                <b>加入房间</b>
                <small>JOIN PRIVATE LINK</small>
                <p>输入六位房间码，接入对方的校准终端。</p>
                <div class="code-field">
                  <input name="code" data-join-code maxlength="6" autocomplete="off" autocapitalize="characters" placeholder="A2C4E6" />
                  <button class="btn btn-ghost btn-small" type="submit">接入</button>
                </div>
              </div>
            </form>
            <button class="action-card action-subtle" type="button" data-action="practice">
              <span class="action-index">LOCAL</span>
              <span class="action-text"><b>单人校准</b><small>PRACTICE / NO ACCOUNT REQUIRED</small></span>
              <span class="action-arrow">→</span>
            </button>
          </section>

          <section class="archive">
            <div class="section-heading">
              <span class="micro-label">ARCHIVE / 受试者档案</span>
              <h2>段位记录</h2>
              <button class="icon-button" type="button" data-action="refresh-leaderboard" title="刷新档案">↻</button>
            </div>
            <div class="archive-list" data-leaderboard>
              <div class="archive-empty">READING ARCHIVE…</div>
            </div>
            <p class="archive-note">仅显示至少完成过一场联机校准的受试者。段位由服务器结算。</p>
          </section>

          <aside class="briefing">
            <div class="section-heading">
              <span class="micro-label">BRIEFING / 协议简报</span>
              <h2>规则</h2>
            </div>
            <ol class="briefing-list">
              ${PROTOCOL_ORDER.map(
                (id) => `<li><span>${roman(id)}</span><b>${PROTOCOLS[id].nameZh}</b><small>${PROTOCOLS[id].nameEn}</small></li>`,
              ).join('')}
            </ol>
            <div class="briefing-note">
              <p><b>唯一规则</b>：看屏幕上那行字的<b>墨色</b>，按下写着这个颜色名称的按键（如“红色”“白色”）。按键自己的字色与白灯都是干扰；每个颜色词一局只出现一次，字色也不重复。</p>
              <p><b>四联屏</b>：发白的屏幕才是同步源。目标要么四屏一致，要么只出现在一块屏上，其余屏只显示无意义图像或色块。白色背景上逆时针变暗的圆盘就是剩余时间。</p>
              <p><b>三根灯管</b>：读错一次或没读到，烧断一根；烧断第三根时本局终止。联机时右侧是你、左侧是对手，1P 蓝、2P 红，座位每局随机。</p>
              <p><b>协议推进</b>：同步屏 4 → 2 → 1，点亮按键 4 → 8，屏幕损伤逐级加重，响应窗口 3.8 秒 → 2.15 秒。协议六会在窗口中途把目标换到另一块屏，规则不变。</p>
            </div>
            <button class="btn btn-ghost btn-small" type="button" data-action="settings">校准设置</button>
          </aside>
        </div>
      </section>`,
    );
    void this.loadLeaderboard();
  }

  private topbar(account: string): string {
    const archive = this.lore.getOverallProgress();
    return `
      <header class="topbar">
        <button class="brand" type="button" data-action="home">
          ${eyeMark('brand-mark')}
          <span class="brand-copy"><b>VESPER</b><small>BEHAVIOURAL RESEARCH</small></span>
        </button>
        <div class="topbar-right">
          <span class="build-tag">${esc(BUILD_LABEL)}</span>
          ${account}
          <button class="archive-link" type="button" data-action="archive" title="碎片档案室">
            <span>ARCHIVE</span><b>${archive.fragmentsOwned}/${archive.fragmentsTotal}</b>
          </button>
          <button class="icon-button" type="button" data-action="settings" aria-label="校准设置">SET</button>
        </div>
      </header>`;
  }

  private async onAuthChange(snapshot: AuthSnapshot | null): Promise<void> {
    void this.lore.syncRemote();
    if (!snapshot) {
      const hadProfile = Boolean(this.profile);
      this.profile = null;
      if (this.viewName === 'lobby') this.renderHome();
      else if (this.viewName === 'home' && hadProfile) this.renderHome();
      return;
    }
    if (this.profile?.uid === snapshot.userId) return;
    await this.refreshProfile();
  }

  private async refreshProfile(): Promise<void> {
    if (this.profilePromise) return this.profilePromise;
    this.profilePromise = (async () => {
      try {
        const token = await this.auth.token();
        if (!token) {
          this.profile = null;
          return;
        }
        const profile = await fetchMe(token);
        this.profile = profile;
        if (this.viewName === 'lobby') this.renderLobby();
        else if (this.viewName === 'home') this.renderHome();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          this.profile = null;
        } else {
          this.showToast('档案读取失败，请稍后重试。');
        }
      } finally {
        this.profilePromise = null;
      }
    })();
    return this.profilePromise;
  }

  private async ensureProfileAndRender(): Promise<void> {
    const token = await this.auth.token();
    if (!token) {
      this.navigateAuth('lobby');
      return;
    }
    try {
      this.profile = await fetchMe(token);
      if (this.viewName === 'lobby') this.renderLobby();
    } catch {
      this.navigateAuth('lobby');
    }
  }

  private async enterApp(): Promise<void> {
    await this.audio.init();
    this.audio.playBoot();
    this.audio.resume();
    if (!this.profile) {
      const token = await this.auth.token();
      if (token) void this.refreshProfile();
    }
    this.renderHome();
  }

  private navigateAuth(intent: AuthIntent): void {
    this.authIntent = intent;
    this.renderAuth();
    this.view.querySelector<HTMLInputElement>('input[name="email"]')?.focus();
  }

  private async handleOnlineIntent(): Promise<void> {
    if (AUTH_MODE === 'none') {
      this.showToast('联机模块未配置：目前只能进行单人校准。');
      return;
    }
    if (!this.profile) {
      this.navigateAuth('lobby');
      return;
    }
    this.renderLobby();
  }

  private startSolo(): void {
    this.closeModal();
    this.quickMatch?.cancel();
    this.quickMatch = null;
    this.online?.destroy();
    this.online = null;
    this.solo?.destroy();
    this.solo = null;
    const name = this.profile?.name ?? '受试者';
    this.setScreen('game', '<div class="game-mount" id="game-mount"></div>');
    void this.audio.resume();
    const mount = this.view.querySelector<HTMLElement>('#game-mount')!;
    this.solo = new SoloSession({
      container: mount,
      audio: this.audio,
      name,
      lore: this.lore,
      onExit: () => this.exitSolo(),
      onRestart: () => {
        this.exitSolo(false);
        this.startSolo();
      },
      onOpenArchive: () => this.openArchive(),
    });
  }

  private exitSolo(render = true): void {
    const solo = this.solo;
    this.solo = null;
    solo?.destroy();
    if (render) this.renderHome();
  }

  private async createOnlineRoom(): Promise<void> {
    const token = await this.auth.token();
    if (!token) {
      this.navigateAuth('lobby');
      return;
    }
    try {
      const { code } = await createRoom(token);
      this.openOnlineRoom(code);
    } catch (error) {
      this.handleApiError(error);
    }
  }

  private async joinOnlineRoom(codeInput: HTMLInputElement | null): Promise<void> {
    const code = sanitizeRoomCode(codeInput?.value ?? '');
    if (!code) {
      this.showToast('请输入有效的六位房间码。');
      codeInput?.focus();
      return;
    }
    const token = await this.auth.token();
    if (!token) {
      this.navigateAuth('lobby');
      return;
    }
    try {
      const result = await joinRoom(token, code);
      this.openOnlineRoom(result.code);
    } catch (error) {
      this.handleApiError(error);
    }
  }

  private openOnlineRoom(code: string): void {
    if (!this.profile) {
      this.navigateAuth('lobby');
      return;
    }
    this.closeModal();
    this.solo?.destroy();
    this.solo = null;
    this.online?.destroy();
    this.online = null;
    this.setScreen('game', '<div class="game-mount" id="game-mount"></div>');
    const mount = this.view.querySelector<HTMLElement>('#game-mount')!;
    this.online = new OnlineSession({
      container: mount,
      code,
      auth: this.auth,
      profile: this.profile,
      audio: this.audio,
      lore: this.lore,
      onExit: () => this.exitOnline(),
      onProfileRefresh: () => {
        void this.refreshProfile();
      },
      onOpenArchive: () => this.openArchive(),
    });
    this.showToast(`房间 ${code} 已接入`);
  }

  private exitOnline(): void {
    this.online = null;
    this.renderLobby();
  }

  private openQuickMatch(): void {
    if (!this.profile) {
      this.navigateAuth('lobby');
      return;
    }
    if (this.quickSearching) return;
    this.quickSearching = true;
    this.openModal(
      `
      <div class="quick-card">
        <span class="seal">MATCHMAKING</span>
        <div class="quick-eye">${eyeMark('')}</div>
        <p class="micro-label">DUAL SUBJECT PROTOCOL</p>
        <h2>寻找受试者</h2>
        <p class="quick-status" data-quick-status>OPENING CHANNEL…</p>
        <p class="quick-sub">系统会优先匹配段位接近的受试者。你可以随时取消。</p>
        <div class="quick-scanline" aria-hidden="true"><i></i></div>
        <button class="btn btn-ghost" type="button" data-action="quick-cancel">取消搜索 / CANCEL</button>
      </div>`,
      {
        dismissible: false,
        onClose: () => {
          if (this.quickSearching) {
            this.quickMatch?.cancel();
            this.quickMatch = null;
            this.quickSearching = false;
          }
        },
      },
    );
    this.quickMatch = startQuickMatch(this.auth, {
      onStatus: (text) => this.setQuickStatus(text),
      onMatched: (code) => {
        this.quickSearching = false;
        this.quickMatch = null;
        this.closeModal(false);
        this.openOnlineRoom(code);
      },
      onError: (message) => {
        this.quickSearching = false;
        this.quickMatch = null;
        this.setQuickStatus(message, true);
        const button = this.modalRoot.querySelector<HTMLButtonElement>('[data-action="quick-cancel"]');
        if (button) button.textContent = '关闭 / CLOSE';
      },
    });
  }

  private setQuickStatus(text: string, error = false): void {
    const node = this.modalRoot.querySelector<HTMLElement>('[data-quick-status]');
    if (node) {
      node.textContent = text;
      node.classList.toggle('is-error', error);
    }
  }

  private closeQuickSearch(): void {
    this.quickMatch?.cancel();
    this.quickMatch = null;
    this.quickSearching = false;
    this.closeModal();
  }

  private async loadLeaderboard(): Promise<void> {
    const container = this.view.querySelector<HTMLElement>('[data-leaderboard]');
    if (!container) return;
    const request = ++this.leaderboardRequest;
    container.innerHTML = '<div class="archive-empty">READING ARCHIVE…</div>';
    try {
      const leaders = await fetchLeaderboard(20);
      if (request !== this.leaderboardRequest) return;
      const current = this.view.querySelector<HTMLElement>('[data-leaderboard]');
      if (!current) return;
      if (!leaders.length) {
        current.innerHTML = '<div class="archive-empty">NO VERIFIED SUBJECTS / 暂无记录</div>';
        return;
      }
      current.innerHTML = leaders
        .map(
          (entry, index) => `
          <div class="archive-row ${entry.uid === this.profile?.uid ? 'is-self' : ''}">
            <span class="archive-rank">${String(index + 1).padStart(2, '0')}</span>
            <span class="archive-name">${esc(entry.name)}<small>${esc(rankTitle(entry.rating))}</small></span>
            <span class="archive-wl">${entry.wins}W / ${entry.losses}L</span>
            <b class="archive-rating">${entry.rating}</b>
          </div>`,
        )
        .join('');
    } catch {
      if (request !== this.leaderboardRequest) return;
      const current = this.view.querySelector<HTMLElement>('[data-leaderboard]');
      if (current) current.innerHTML = '<div class="archive-empty">ARCHIVE UNAVAILABLE</div>';
    }
  }

  private openArchive(section?: LoreSection): void {
    this.archiveSection = section ?? this.archiveSection;
    this.archiveDocId = null;
    void this.lore.syncRemote();
    this.openModal(renderArchiveModal(this.lore, this.archiveSection, this.archiveDocId), {
      wide: true,
    });
  }

  private refreshArchive(): void {
    const card = this.modalRoot.querySelector<HTMLElement>('.modal-card');
    if (!card) {
      this.openArchive(this.archiveSection);
      return;
    }
    card.innerHTML = renderArchiveModal(this.lore, this.archiveSection, this.archiveDocId);
    card.scrollTop = 0;
  }

  private openArchiveZoom(src: string, alt: string): void {
    const card = this.modalRoot.querySelector<HTMLElement>('.modal-card');
    if (!card) return;
    card.insertAdjacentHTML(
      'beforeend',
      `<div class="archive-lightbox" data-action="archive-zoom-close">
         <button class="icon-button" type="button" data-action="archive-zoom-close">CLOSE</button>
         <figure><img src="${esc(src)}" alt="${esc(alt)}" /><figcaption>${esc(alt)}</figcaption></figure>
       </div>`,
    );
  }

  private openSettings(): void {
    this.openModal(
      `
      <div class="settings-card dossier">
        <div class="settings-head">
          <div>
            <span class="micro-label">CHROMA-06 / SYSTEM PREFERENCES</span>
            <h2>校准设置</h2>
          </div>
          <button class="icon-button" type="button" data-action="close-modal" aria-label="关闭">CLOSE</button>
        </div>

        <div class="settings-group">
          <h3>画质与帧率 / GRAPHICS &amp; FRAME RATE</h3>
          <label class="select-row">
            <span>渲染质量 <small>Textures, particles, blur</small></span>
            <select data-setting="graphicsQuality">
              <option value="low" ${this.settings.graphicsQuality === 'low' ? 'selected' : ''}>低 / LOW</option>
              <option value="medium" ${this.settings.graphicsQuality === 'medium' ? 'selected' : ''}>中 / MEDIUM</option>
              <option value="high" ${this.settings.graphicsQuality === 'high' ? 'selected' : ''}>高 / HIGH</option>
            </select>
          </label>
          <label class="select-row">
            <span>效果帧率目标 <small>Animation budget</small></span>
            <select data-setting="frameTarget">
              <option value="30" ${this.settings.frameTarget === '30' ? 'selected' : ''}>兼容 / 30</option>
              <option value="60" ${this.settings.frameTarget === '60' ? 'selected' : ''}>平衡 / 60</option>
              <option value="120" ${this.settings.frameTarget === '120' ? 'selected' : ''}>完整 / 120+</option>
            </select>
          </label>
          <label class="select-row">
            <span>帧数显示 <small>FPS COUNTER</small></span>
            <select data-setting="showFps">
              <option value="on" ${this.settings.showFps ? 'selected' : ''}>显示 / ON</option>
              <option value="off" ${!this.settings.showFps ? 'selected' : ''}>隐藏 / OFF</option>
            </select>
          </label>
          <p class="settings-hint">30 档关闭颗粒、动态扫描线与全屏叠层动画；60 档保留静态扫描线；120+ 启用完整动态效果。这是效果成本档位，不强制锁帧；不影响题目、计时或判定。</p>
        </div>

        <div class="settings-group">
          <h3>音量 / VOLUME</h3>
          <label class="range-row">
            <span>主音量 <small>MASTER OUTPUT</small></span>
            <input type="range" min="0" max="100" value="${Math.round(this.settings.masterVolume * 100)}" data-setting="masterVolume" />
            <output data-output="masterVolume">${Math.round(this.settings.masterVolume * 100)}%</output>
          </label>
          <label class="range-row">
            <span>环境与音效 <small>AMBIENCE / SFX</small></span>
            <input type="range" min="0" max="100" value="${Math.round(this.settings.effectsVolume * 100)}" data-setting="effectsVolume" />
            <output data-output="effectsVolume">${Math.round(this.settings.effectsVolume * 100)}%</output>
          </label>
          <p class="settings-hint">颜色广播与控制台声音固定开启，不能单独静音。</p>
        </div>

        <div class="settings-group">
          <h3>网络诊断 / NETWORK</h3>
          <div class="diag-grid">
            <div><span>HTTP API</span><b data-diag-http>—</b></div>
            <div><span>WEBSOCKET</span><b data-diag-ws>—</b></div>
            <div><span>SUPABASE AUTH</span><b data-diag-auth>—</b></div>
          </div>
          <p class="diag-note" data-diag-note>未运行。联机前建议测试一次；结果只代表当前网络。</p>
          <button class="btn btn-ghost btn-small" type="button" data-action="run-diagnostics">运行网络诊断 / RUN TEST</button>
        </div>

        <div class="settings-foot">
          <p>颜色广播始终开启。本作按固定强度呈现闪光、恐怖与声音，不提供字幕、色盲辅助或低刺激模式；无法接受这些刺激或颜色识别要求时，请不要游玩。</p>
          <button class="btn btn-primary" type="button" data-action="close-modal">应用并关闭 / APPLY</button>
        </div>
      </div>`,
      { dismissible: true },
    );
  }

  private openEditName(): void {
    const name = this.profile?.name ?? '';
    this.openModal(
      `
      <form class="edit-card dossier" data-form="edit-name">
        <span class="micro-label">SUBJECT DOSSIER / AMENDMENT</span>
        <h2>修改受试者代号</h2>
        <label class="field">
          <span>显示名称 / DISPLAY NAME</span>
          <input name="displayName" maxlength="16" value="${esc(name)}" required />
          <small>2–16 个字符，将同步到大厅和段位。</small>
        </label>
        <p class="form-message" data-form-message aria-live="polite"></p>
        <div class="modal-actions">
          <button class="btn btn-primary" type="submit">保存 / SAVE</button>
          <button class="btn btn-ghost" type="button" data-action="close-modal">取消 / CANCEL</button>
        </div>
      </form>`,
      { dismissible: true },
    );
    this.modalRoot.querySelector<HTMLInputElement>('input[name="displayName"]')?.focus();
  }

  private openPasswordModal(recovery: boolean): void {
    if (this.auth.mode !== 'supabase') {
      this.showToast('当前账号模式不支持修改密码。');
      return;
    }
    this.openModal(
      `
      <form class="edit-card dossier" data-form="password-recovery">
        <span class="micro-label">SUBJECT CREDENTIALS / AMENDMENT</span>
        <h2>${recovery ? '设置新密码' : '修改密码'}</h2>
        <p class="auth-intro">${recovery ? '邮箱恢复链接已校验。设置新密码后，当前会话会立即生效。' : '新密码至少 8 位。修改后，其他设备上的旧会话可能需要重新登录。'}</p>
        <label class="field">
          <span>新密码 / NEW PASSPHRASE</span>
          <input name="password" type="password" minlength="8" autocomplete="new-password" required placeholder="至少 8 位" />
        </label>
        <label class="field">
          <span>确认密码 / CONFIRM</span>
          <input name="confirmPassword" type="password" minlength="8" autocomplete="new-password" required placeholder="再次输入" />
        </label>
        <p class="form-message" data-form-message aria-live="polite"></p>
        <div class="modal-actions">
          <button class="btn btn-primary" type="submit">保存 / SAVE</button>
          <button class="btn btn-ghost" type="button" data-action="close-modal">稍后 / LATER</button>
        </div>
      </form>`,
      { dismissible: true },
    );
    this.modalRoot.querySelector<HTMLInputElement>('input[name="password"]')?.focus();
  }

  private openModal(
    html: string,
    options: { dismissible?: boolean; onClose?: () => void; wide?: boolean } = {},
  ): void {
    this.modalDismissible = options.dismissible ?? true;
    this.modalOnClose = options.onClose ?? null;
    this.modalRoot.innerHTML = `<div class="modal-backdrop"><div class="modal-card${
      options.wide ? ' is-wide' : ''
    }">${html}</div></div>`;
    this.modalRoot.classList.add('is-active');
  }

  private closeModal(triggerOnClose = true): void {
    if (!this.modalRoot.classList.contains('is-active')) return;
    const onClose = this.modalOnClose;
    this.modalOnClose = null;
    this.modalRoot.classList.remove('is-active');
    this.modalRoot.innerHTML = '';
    if (triggerOnClose) onClose?.();
  }

  private showToast(message: string): void {
    this.toastEl.textContent = message;
    this.toastEl.classList.add('is-active');
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toastEl.classList.remove('is-active');
      this.toastTimer = null;
    }, 2600);
  }

  private handleApiError(error: unknown): void {
    if (error instanceof ApiError) {
      if (error.status === 401) {
        this.showToast('会话已过期，请重新登录。');
        this.navigateAuth('lobby');
        return;
      }
      if (error.code === 'ROOM_FULL') {
        this.showToast('房间已满。');
        return;
      }
      if (error.code === 'ROOM_STARTED') {
        this.showToast('该对局已经开始。');
        return;
      }
      if (error.code === 'ROOM_NOT_FOUND') {
        this.showToast('房间码无效或已失效。');
        return;
      }
      if (error.code === 'INVALID_CODE') {
        this.showToast('房间码格式不正确。');
        return;
      }
      this.showToast(error.code);
      return;
    }
    this.showToast('请求失败，请稍后重试。');
  }

  private onViewClick = (event: Event): void => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target) return;
    const action = target.dataset.action;
    switch (action) {
      case 'enter':
        void this.enterApp();
        break;
      case 'home':
        this.closeModal();
        this.renderHome();
        break;
      case 'auth':
        this.navigateAuth('lobby');
        break;
      case 'lobby':
        void this.handleOnlineIntent();
        break;
      case 'online':
        void this.handleOnlineIntent();
        break;
      case 'practice':
        this.startSolo();
        break;
      case 'settings':
        this.openSettings();
        break;
      case 'archive':
        this.openArchive();
        break;
      case 'signout':
        void this.signOut();
        break;
      case 'quick-match':
        this.openQuickMatch();
        break;
      case 'create-room':
        void this.createOnlineRoom();
        break;
      case 'refresh-leaderboard':
        void this.loadLeaderboard();
        break;
      case 'edit-name':
        this.openEditName();
        break;
      case 'change-password':
        this.openPasswordModal(false);
        break;
      case 'auth-tab': {
        const tab = target.dataset.tab === 'signup' ? 'signup' : 'login';
        this.authTab = tab;
        const form = this.view.querySelector<HTMLFormElement>('form[data-form="auth"]');
        if (form) form.dataset.authTab = tab;
        const tabs = this.view.querySelector<HTMLElement>('.form-tabs');
        if (tabs) tabs.dataset.authTab = tab;
        this.view.querySelectorAll<HTMLElement>('[data-action="auth-tab"]').forEach((button) => {
          button.classList.toggle('is-active', button.dataset.tab === tab);
        });
        const submit = this.view.querySelector<HTMLElement>('[data-auth-submit]');
        if (submit) submit.textContent = tab === 'login' ? '进入档案 / SIGN IN' : '提交登记 / REGISTER';
        break;
      }
      case 'forgot-password':
        void this.forgotPassword();
        break;
      default:
        break;
    }
  };

  private onViewSubmit = (event: Event): void => {
    const form = event.target as HTMLFormElement;
    const formName = form.dataset.form;
    if (!formName) return;
    event.preventDefault();
    if (formName === 'auth') void this.submitAuth(form);
    else if (formName === 'dev-auth') void this.submitDevAuth(form);
    else if (formName === 'join-room') {
      void this.joinOnlineRoom(form.querySelector<HTMLInputElement>('[data-join-code]'));
    }
  };

  private onModalClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('modal-backdrop')) {
      if (this.modalDismissible) this.closeModal();
      return;
    }
    const actionTarget = target.closest<HTMLElement>('[data-action]');
    if (!actionTarget) return;
    const action = actionTarget.dataset.action;
    if (action === 'close-modal') {
      this.closeModal();
    } else if (action === 'archive-section') {
      const section = actionTarget.dataset.section as LoreSection | undefined;
      if (section) {
        this.archiveSection = section;
        this.archiveDocId = null;
        this.refreshArchive();
      }
    } else if (action === 'archive-doc') {
      this.archiveDocId = actionTarget.dataset.doc ?? null;
      this.refreshArchive();
    } else if (action === 'archive-back') {
      this.archiveDocId = null;
      this.refreshArchive();
    } else if (action === 'archive-zoom') {
      this.openArchiveZoom(actionTarget.dataset.src ?? '', actionTarget.dataset.alt ?? '');
    } else if (action === 'archive-zoom-close') {
      this.modalRoot.querySelector('.archive-lightbox')?.remove();
    } else if (action === 'quick-cancel') {
      this.closeQuickSearch();
    } else if (action === 'run-diagnostics') {
      void this.runDiagnostics();
    }
  };

  private onModalSubmit = (event: Event): void => {
    const form = event.target as HTMLFormElement;
    if (form.dataset.form === 'edit-name') {
      event.preventDefault();
      void this.submitName(form);
    } else if (form.dataset.form === 'password-recovery') {
      event.preventDefault();
      void this.submitPassword(form);
    }
  };

  private onModalInput = (event: Event): void => {
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    const key = input.dataset.setting as keyof Settings | undefined;
    if (!key) return;
    if (key === 'graphicsQuality') {
      if (!isGraphicsQuality(input.value)) return;
      this.settings.graphicsQuality = input.value;
      applyGraphicsQuality(input.value);
    } else if (key === 'frameTarget') {
      if (!isFrameTarget(input.value)) return;
      this.settings.frameTarget = input.value;
      applyFrameTarget(input.value);
    } else if (key === 'showFps') {
      this.settings.showFps = input.value === 'on';
      this.fps?.setVisible(this.settings.showFps);
    } else if (key === 'masterVolume' || key === 'effectsVolume') {
      const value = Number(input.value) / 100;
      this.settings[key] = value;
      const output = this.modalRoot.querySelector<HTMLOutputElement>(`[data-output="${key}"]`);
      if (output) output.textContent = `${Math.round(value * 100)}%`;
    } else {
      return;
    }
    saveSettings(this.settings);
    this.audio.setSettings(this.settings);
  };

  private async runDiagnostics(): Promise<void> {
    const button = this.modalRoot.querySelector<HTMLButtonElement>('[data-action="run-diagnostics"]');
    const note = this.modalRoot.querySelector<HTMLElement>('[data-diag-note]');
    if (!note) return;
    if (button) button.disabled = true;
    note.textContent = '正在测试 HTTP、WebSocket 与认证端点…';
    try {
      const report = await runNetworkDiagnostics();
      const setRow = (selector: string, value: string, ok: boolean | null) => {
        const node = this.modalRoot.querySelector<HTMLElement>(selector);
        if (!node) return;
        node.textContent = value;
        node.dataset.state = ok === null ? 'unknown' : ok ? 'good' : 'bad';
      };
      setRow('[data-diag-http]', formatLatency(report.http), report.http.ok);
      setRow('[data-diag-ws]', formatLatency(report.websocket), report.websocket.ok);
      setRow(
        '[data-diag-auth]',
        report.supabase ? formatLatency(report.supabase) : 'NOT CONFIGURED',
        report.supabase ? report.supabase.ok : null,
      );
      const time = new Date(report.checkedAt).toLocaleTimeString();
      note.textContent = `${report.online ? 'ONLINE' : 'OFFLINE'} · ${time} · 格式为 min / avg / max；WS 含边缘往返。`;
    } catch {
      note.textContent = '诊断失败。检查当前网络或 Worker 是否可访问。';
    } finally {
      if (button) button.disabled = false;
    }
  }

  private onGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    const lightbox = this.modalRoot.querySelector('.archive-lightbox');
    if (lightbox) {
      lightbox.remove();
      return;
    }
    if (this.modalRoot.classList.contains('is-active') && this.modalDismissible) {
      this.closeModal();
    }
  };

  private async submitAuth(form: HTMLFormElement): Promise<void> {
    const email = (form.elements.namedItem('email') as HTMLInputElement | null)?.value.trim() ?? '';
    const password = (form.elements.namedItem('password') as HTMLInputElement | null)?.value ?? '';
    const displayName =
      (form.elements.namedItem('displayName') as HTMLInputElement | null)?.value ?? '';
    const message = form.querySelector<HTMLElement>('[data-form-message]');
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const tab = (form.dataset.authTab === 'signup' ? 'signup' : 'login') as AuthTab;
    if (!email || !password) {
      if (message) message.textContent = '请填写邮箱与密码。';
      return;
    }
    if (tab === 'signup') {
      const name = sanitizeDisplayName(displayName);
      if (!name) {
        if (message) message.textContent = '受试者代号需为 2–16 个中文、字母、数字或 _-.。';
        return;
      }
      if (password.length < 8) {
        if (message) message.textContent = '密码至少需要 8 位。';
        return;
      }
    }
    if (submit) submit.disabled = true;
    if (message) message.textContent = tab === 'login' ? '验证中…' : '建立档案中…';
    try {
      if (tab === 'login') {
        await this.auth.signIn(email, password);
        await this.refreshProfile();
        if (this.profile) {
          const intent = this.authIntent ?? 'lobby';
          this.authIntent = null;
          if (intent === 'lobby') this.renderLobby();
          else this.renderHome();
        } else if (message) {
          message.textContent = '账号已验证，但档案读取失败，请重试。';
        }
      } else {
        const result = await this.auth.signUp(email, password, displayName.trim());
        if (result.needsConfirmation) {
          if (message) {
            message.textContent = '确认邮件已发送。请先完成邮箱验证，再返回登录。';
          }
          return;
        }
        await this.refreshProfile();
        if (this.profile) {
          const intent = this.authIntent ?? 'lobby';
          this.authIntent = null;
          if (intent === 'lobby') this.renderLobby();
          else this.renderHome();
        }
      }
    } catch (error) {
      if (message) message.textContent = error instanceof Error ? error.message : '操作失败。';
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  private async submitDevAuth(form: HTMLFormElement): Promise<void> {
    const displayName =
      (form.elements.namedItem('displayName') as HTMLInputElement | null)?.value ?? '';
    const name = sanitizeDisplayName(displayName);
    const message = form.querySelector<HTMLElement>('[data-form-message]');
    if (!name) {
      if (message) message.textContent = '受试者代号需为 2–16 个中文、字母、数字或 _-.。';
      return;
    }
    try {
      this.auth.devSignIn(name);
      await this.refreshProfile();
      const intent = this.authIntent ?? 'lobby';
      this.authIntent = null;
      if (intent === 'lobby') this.renderLobby();
      else this.renderHome();
    } catch (error) {
      if (message) message.textContent = error instanceof Error ? error.message : '本机档案创建失败。';
    }
  }

  private async forgotPassword(): Promise<void> {
    const input = this.view.querySelector<HTMLInputElement>('input[name="email"]');
    const message = this.view.querySelector<HTMLElement>('[data-form-message]');
    const email = input?.value.trim() ?? '';
    if (!email) {
      if (message) message.textContent = '请先填写邮箱。';
      input?.focus();
      return;
    }
    try {
      await this.auth.requestPasswordReset(email);
      if (message) message.textContent = '重置邮件已发送，请检查收件箱与垃圾箱。';
    } catch (error) {
      if (message) message.textContent = error instanceof Error ? error.message : '发送失败。';
    }
  }

  private async submitName(form: HTMLFormElement): Promise<void> {
    const input = form.elements.namedItem('displayName') as HTMLInputElement | null;
    const message = form.querySelector<HTMLElement>('[data-form-message]');
    const name = sanitizeDisplayName(input?.value ?? '');
    if (!name) {
      if (message) message.textContent = '名称需为 2–16 个中文、字母、数字或 _-.。';
      return;
    }
    const token = await this.auth.token();
    if (!token) {
      this.closeModal();
      this.navigateAuth('lobby');
      return;
    }
    try {
      const profile = await updateDisplayName(token, name);
      this.profile = profile;
      this.closeModal();
      this.renderLobby();
      this.showToast('档案已更新。');
    } catch (error) {
      if (message) {
        message.textContent = error instanceof ApiError ? error.code : '保存失败。';
      }
    }
  }

  private async submitPassword(form: HTMLFormElement): Promise<void> {
    const password =
      (form.elements.namedItem('password') as HTMLInputElement | null)?.value ?? '';
    const confirm =
      (form.elements.namedItem('confirmPassword') as HTMLInputElement | null)?.value ?? '';
    const message = form.querySelector<HTMLElement>('[data-form-message]');
    if (password.length < 8) {
      if (message) message.textContent = '新密码至少需要 8 位。';
      return;
    }
    if (password !== confirm) {
      if (message) message.textContent = '两次输入的密码不一致。';
      return;
    }
    try {
      await this.auth.updatePassword(password);
      this.closeModal();
      this.showToast('密码已更新。');
      await this.refreshProfile();
    } catch (error) {
      if (message) message.textContent = error instanceof Error ? error.message : '密码更新失败。';
    }
  }

  private async signOut(): Promise<void> {
    this.quickMatch?.cancel();
    this.quickMatch = null;
    this.quickSearching = false;
    this.closeModal();
    this.online?.destroy();
    this.online = null;
    this.solo?.destroy();
    this.solo = null;
    await this.auth.signOut();
    this.profile = null;
    this.renderHome();
    this.showToast('已退出受试者档案。');
  }
}

function roman(id: number): string {
  return ['I', 'II', 'III', 'IV', 'V', 'VI'][id - 1] ?? '—';
}

function renderAuthModeBadge(): void {
  document.documentElement.dataset.authMode = AUTH_MODE;
  document.documentElement.dataset.hasSupabase = String(HAS_SUPABASE);
}
